import { cloneDeep } from 'lodash';
import {
  BufferAttribute,
  Color,
  Group,
  Matrix3,
  Matrix4,
  OrthographicCamera,
  PerspectiveCamera,
  Points,
  TypedArray,
  Vector3,
} from 'three';

import { frameAnnoStore } from '@/features/anno/stores';
import { OBB } from '@/lib/three';
import type { SelectedSegment } from '@/stores';
import type { ElementAnno, Segmentation3d, Statistic } from '@/types';
import { RawPoints, Stage } from '@/utils/render';

import { pointInPolyWindingNumber } from '../graphics/PointInPolygon';

export type Segmentations = Array<ElementAnno | Segmentation3d>;

type Segment = { statistic: Statistic[]; category: string[]; instance_id: number[] };
type SegmentData = Record<number, Segment>;
type Conditional<T extends any> = Record<string, T>;

let flieStartInx = -1;

export class SseEditor {
  static segmentData: SegmentData = [];

  private object: Group;
  private store: typeof frameAnnoStore;
  private curFrame: { name: string; index: number } | undefined;
  private overlayFrames: { name: string; index: number }[] | undefined;
  private colorArray: Conditional<TypedArray>;
  private categoryID: Conditional<TypedArray>;
  private hiddenIndices: Conditional<TypedArray>;
  private onlyVisibleIndices: Conditional<TypedArray>;
  private clipArray: Conditional<TypedArray>;
  private selectArray: Conditional<TypedArray>;
  private unlabeledHighlightArray: Conditional<TypedArray>;
  private focusMaskArray: Conditional<TypedArray>; // 聚焦模式独立遮罩数组
  private frustrumIndices: Conditional<number[]>;
  private pixelProjections: Conditional<Array<number[]>>;
  private selection: Conditional<number[]>;
  private isHidden: boolean;

  constructor(object: Group, store: typeof frameAnnoStore) {
    this.object = object;
    this.store = store;
    this.curFrame = undefined;
    this.overlayFrames = undefined;
    this.colorArray = {};
    this.categoryID = {};
    this.hiddenIndices = {};
    this.clipArray = {};
    this.selectArray = {};
    this.unlabeledHighlightArray = {};
    this.focusMaskArray = {}; // 聚焦模式独立遮罩数组
    this.onlyVisibleIndices = {};
    this.frustrumIndices = {};
    this.pixelProjections = {};
    this.selection = {};
    this.isHidden = false;
  }

  static initElementSegments = (data: Segmentations = []) => {
    SseEditor.segmentData = data.reduce((acc: SegmentData, segmentation, inx: number) => {
      const hasSegmentation = segmentation && 'segmentation3d' in segmentation;
      const segmentation3d = hasSegmentation ? segmentation.segmentation3d : segmentation;
      if (!segmentation3d) {
        return acc;
      }
      const { result = {}, statistic = [] } = segmentation3d as Segmentation3d;
      let key;
      if (hasSegmentation) {
        if (flieStartInx === -1) {
          flieStartInx = segmentation.index;
        }
        key = segmentation.index - flieStartInx;
      } else {
        key = inx;
      }
      acc[key] = {
        category: result.category ?? [],
        instance_id: result.instance_id ?? [],
        statistic,
      };

      return acc;
    }, {});
  };

  public initRawdata = (points: Points, index: number) => {
    const { initSegments } = this.store.getState();
    const name = points.name;
    const count = (points as Points).geometry.attributes.position.array.length / 3;
    this.initColorArray(name, count, index);
    initSegments();
    const { category, instance_id } = SseEditor.segmentData[index];
    this.initSettings(name, category, instance_id);
    this.updatePointCloudColor(points as Points);
  };

  private initColorArray = (name: string, length: number, inx: number) => {
    const { getColor, updateStatistic, setPointsNum } = this.store.getState();

    this.categoryID[name] = new Float32Array(length).fill(0);
    this.colorArray[name] = new Float32Array(length * 3).fill(0.8);

    const segment = SseEditor.segmentData[inx];
    if (segment) {
      const { category, statistic } = segment;
      let statistics = [];
      statistics[inx] = statistic;
      updateStatistic(statistics);
      category?.forEach((categoryName, index) => {
        if (statistic?.find((item) => item.category_name === categoryName)) {
          this.categoryID[name][index] = 1;
          const { r, g, b } = new Color(getColor(categoryName));
          this.colorArray[name].set([r, g, b], index * 3);
        }
      });
    } else {
      SseEditor.segmentData[inx] = {
        category: new Array(length).fill(''),
        instance_id: new Array(length).fill(0),
        statistic: [],
      };
    }

    setPointsNum(length, inx);
  };

  public addRawdata = () => {
    const { job, overlayFrames, initSegments } = this.store.getState();
    if (!overlayFrames || !job) return;
    const { startIndex, num } = overlayFrames;
    
    // 确保 overlayFrames 数组已初始化
    if (!this.overlayFrames) {
      this.overlayFrames = [];
    }
    
    for (let i = 0; i <= num; i++) {
      const index = startIndex + i;
      const name = job.elements[index].datas[0].name;
      const curFrame = this.overlayFrames.find((frame) => frame.name === name);
      if (!curFrame) {
        const points = this.object.children.find((obj) => obj.name === name);
        if (points) {
          this.overlayFrames.push({ name, index });
          const count = (points as Points).geometry.attributes.position.array.length / 3;
          this.initColorArray(name, count, index);
        }
      }
    }
    initSegments();
    this.overlayFrames.forEach(({ name, index }) => {
      if (!this.selectArray[name]?.length) {
        const { category, instance_id } = SseEditor.segmentData[index];
        // initSettings 中会根据 focusMode.isActive 自动初始化 focusMaskArray
        this.initSettings(name, category, instance_id);
      }
    });

    this.updatePointCloudColor();

    // 如果处于漏标检测模式，更新漏标数量
    const { unlabeledDetection, setUnlabeledDetection } = this.store.getState();
    if (unlabeledDetection?.isActive) {
      const count = this.highlightUnlabeledPoints(true);
      setUnlabeledDetection(true, count);
    }
  };

  initData = () => {
    const { overlayFrames, currentElementIndex } = this.store.getState();
    if (overlayFrames) {
      this.overlayFrames = [];
      this.addRawdata();
    } else {
      this.object.children.forEach((points) => {
        this.curFrame = { name: points.name, index: currentElementIndex };
        this.initRawdata(points as Points, currentElementIndex);
      });
    }
  };

  private initSettings = (name: string, categoryArray: string[], instanceIDArray: number[]) => {
    const { selectedSegment, getOnlySetting, getSetting } = this.store.getState();
    const hasOnlySetting = getOnlySetting();

    const length = categoryArray.length;
    this.onlyVisibleIndices[name] = new Uint8Array(length);
    this.selectArray[name] = new Float32Array(length);
    this.clipArray[name] = new Float32Array(length);
    this.hiddenIndices[name] = new Uint8Array(length);
    
    // 初始化聚焦遮罩数组：默认全部可见（0）
    // 聚焦模式下不允许叠帧变化，所以新帧只会在非聚焦模式下添加
    this.focusMaskArray[name] = new Float32Array(length).fill(0);

    categoryArray?.forEach((category, inx) => {
      const instanceID = instanceIDArray[inx];
      const setting = getSetting(category, instanceID);

      if (hasOnlySetting && setting?.isOnlyShow) {
        this.addOnlyShowIndex(name, inx);
        this.updateVisibility(name, inx, 0);
      } else if (setting?.isHide) {
        this.updateVisibility(name, inx, 1);
      }

      // 更新选中效果数组
      const isSelected = Number(
        selectedSegment?.hasInstanceId ? instanceID === selectedSegment?.instanceId : category === selectedSegment?.name
      );
      this.selectArray[name][inx] = isSelected;
    });
  };

  private invalidatePoints = (type: string, child: Points) => {
    const geometry = child.geometry;
    switch (type) {
      case 'color':
        if (this.colorArray[child.name]) {
          geometry.setAttribute('sseColor', new BufferAttribute(this.colorArray[child.name], 3));
          geometry.attributes.sseColor.needsUpdate = true;
          geometry.setAttribute('categoryID', new BufferAttribute(this.categoryID[child.name], 1));
          geometry.attributes.categoryID.needsUpdate = true;
        }
        break;

      case 'position':
        if (this.clipArray[child.name]) {
          geometry.setAttribute('hidePoint', new BufferAttribute(this.clipArray[child.name], 1));
          geometry.attributes.hidePoint.needsUpdate = true;
        }
        break;

      case 'section':
        if (this.selectArray[child.name]) {
          geometry.setAttribute('selectPoint', new BufferAttribute(this.selectArray[child.name], 1));
          geometry.attributes.selectPoint.needsUpdate = true;
        }
        break;

      case 'unlabeled':
        if (this.unlabeledHighlightArray[child.name]) {
          geometry.setAttribute('unlabeledHighlight', new BufferAttribute(this.unlabeledHighlightArray[child.name], 1));
          geometry.attributes.unlabeledHighlight.needsUpdate = true;
        }
        break;

      case 'focusMask':
        if (this.focusMaskArray[child.name]) {
          geometry.setAttribute('focusMask', new BufferAttribute(this.focusMaskArray[child.name], 1));
          geometry.attributes.focusMask.needsUpdate = true;
        }
        break;
    }
  };

  private invalidate = (type: string, points?: Points) => {
    if (points) {
      this.invalidatePoints(type, points);
      return;
    }

    this.object.children?.forEach((child) => this.invalidatePoints(type, child as Points));
  };

  public updatePointCloudColor = (points?: Points) =>
    ['color', 'position', 'section', 'focusMask'].forEach((property) => this.invalidate(property, points));

  public updateAllRawdata = () => {
    const { job, overlayFrames, labelStage } = this.store.getState();
    if (!overlayFrames || !job || !labelStage) return;
    const { startIndex, num } = overlayFrames;
    for (let i = 0; i <= num; i++) {
      const index = startIndex + i;
      const name = job.elements[index].datas[0].name;
      const rawObject = labelStage.sights[name]?.rawObject;
      if (rawObject instanceof RawPoints) {
        const points = rawObject.scheduler?.object.children.find((obj) => obj.name === name);
        if (points) {
          rawObject.scheduler?.initRawdata(points as Points, index);
        }
      }
    }
  };

  public removeRawdata = (name: string) => {
    this.overlayFrames = this.overlayFrames?.filter((obj) => obj.name !== name);
    delete this.colorArray[name];
    delete this.categoryID[name];
    delete this.hiddenIndices[name];
    delete this.clipArray[name];
    delete this.selectArray[name];
    delete this.onlyVisibleIndices[name];
    delete this.selection[name];
    delete this.focusMaskArray[name]; // 清理聚焦遮罩数组
  };

  private filterAndProjectPoints = (labelStage: Stage, obb: OBB, camera: OrthographicCamera | PerspectiveCamera) => {
    const vector = new Vector3();
    const { width, height } = labelStage;
    this.object.children.forEach((child) => {
      const cloud = (child as Points).geometry.attributes.position.array;
      const name = child.name;
      if (!this.frustrumIndices[name]) this.frustrumIndices[name] = [];
      if (!this.pixelProjections[name]) this.pixelProjections[name] = [];
      for (let index = 0; index < cloud.length; index += 3) {
        vector.set(cloud[index], cloud[index + 1], cloud[index + 2]);
        if (obb.containsPoint(vector)) {
          this.frustrumIndices[name].push(index / 3);
          vector.project(camera);
          this.pixelProjections[name].push([
            Math.round((vector.x * width) / 2 + width / 2),
            Math.round((-vector.y * height) / 2 + height / 2),
          ]);
        }
      }
    });
  };

  /**
   * 将2D点投影到3D空间中。
   *
   * @param point
   * @param canvasBounds
   * @param camera
   * @returns Vector3 - 投影后的3D向量。
   */
  private projectPoint = (point: number[], canvasBounds: DOMRect, camera: OrthographicCamera | PerspectiveCamera) => {
    const [x, y] = point;
    const { width, height } = canvasBounds;
    const screenX = (x / width) * 2 - 1;
    const screenY = (-y / height) * 2 + 1;
    const vector3D = new Vector3(screenX, screenY, 0).unproject(camera);
    return vector3D;
  };

  private updatePixelProjection = (points: number[][]) => {
    if (!points) return;

    const { labelStage } = this.store.getState();
    const camera = labelStage?.currentSight?.camera;
    if (!camera) return;

    let [minX, minY] = points[0];
    let [maxX, maxY] = points[0];

    points.forEach(([x, y]) => {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    });

    // 计算OBB
    const canvasBounds = labelStage.canvas.getBoundingClientRect();
    const boundaryPoints = [
      this.projectPoint([minX, minY], canvasBounds, camera),
      this.projectPoint([minX, maxY], canvasBounds, camera),
      this.projectPoint([maxX, minY], canvasBounds, camera),
    ];

    const distanceX = boundaryPoints[2].distanceTo(boundaryPoints[0]);
    const distanceY = boundaryPoints[1].distanceTo(boundaryPoints[0]);
    const halfSize = new Vector3(distanceX, distanceY, (camera.far - camera.near) * 2).multiplyScalar(0.5);
    const centerPoint = this.projectPoint([(minX + maxX) / 2, (minY + maxY) / 2], canvasBounds, camera);
    const rotation = new Matrix3().setFromMatrix4(new Matrix4().makeRotationFromEuler(camera.rotation));

    const obb = new OBB();
    obb.center.copy(centerPoint);
    obb.halfSize.copy(halfSize);
    obb.rotation.copy(rotation);

    // 筛选和投影点
    this.frustrumIndices = {};
    this.pixelProjections = {};
    this.filterAndProjectPoints(labelStage, obb, camera);
  };

  /**
   * 选中点云根据操作类型更新数组
   */
  private processSelection = (key: string, index: number, segment: SelectedSegment | null, frameIndex: number) => {
    // 跳过被隐藏的点
    if (this.hiddenIndices[key]?.[index]) return;
    // 跳过被聚焦遮罩隐藏的点（聚焦模式下只能标注聚焦区域内的点）
    if (this.focusMaskArray[key]?.[index] === 1) return;
    
    const { curSegMode } = this.store.getState();

    const categoryID = this.categoryID[key]?.[index];
    const instanceID = SseEditor.segmentData[frameIndex].instance_id?.[index];
    const category = SseEditor.segmentData[frameIndex].category?.[index];
    const isSegmentMatch = segment?.name === category && (segment?.instanceId === instanceID || !segment?.instanceId);

    if (
      curSegMode === 'cover' ||
      (curSegMode === 'add' && !categoryID) ||
      (curSegMode === 'remove' && categoryID && isSegmentMatch)
    ) {
      if (!this.selection[key]) this.selection[key] = [];
      this.selection[key].push(index);

      const needUpdate = curSegMode !== 'remove';
      this.categoryID[key][index] = needUpdate && segment?.name ? 1 : 0;
      SseEditor.segmentData[frameIndex].category[index] = needUpdate ? segment?.name ?? '' : '';
      SseEditor.segmentData[frameIndex].instance_id[index] = needUpdate ? segment?.instanceId ?? 0 : 0;
      this.selectArray[key][index] = needUpdate ? 1 : 0;

      // 实时更新漏标高亮状态：仅在漏标检测模式激活时才更新
      const { unlabeledDetection } = this.store.getState();
      if (this.unlabeledHighlightArray[key] && unlabeledDetection?.isActive) {
        this.unlabeledHighlightArray[key][index] = needUpdate ? 0 : 1;
      }

      if (this.onlyVisibleIndices[key]?.[index] || (needUpdate && this.isHidden)) {
        this.updateVisibility(key, index, 1);
      }
    }
  };

  /**
   * 根据多边形选择点云数据
   *
   * @param selectionPoints 二维空间中的点，用于多边形内点的检测
   */
  private selectByPolygon = (selectionPoints: number[][]) => {
    const { getSetting, getSegSelectInfo, updateStatistic, initSegments } = this.store.getState();

    const selectedSegment = getSegSelectInfo();

    this.updatePixelProjection(selectionPoints);
    this.isHidden = getSetting(selectedSegment?.name ?? '', selectedSegment?.instanceId)?.isHide ? true : false;

    // 遍历 frustrumIndices 检查该点是否在多边形内
    Object.entries(this.frustrumIndices).forEach(([name, indices]) => {
      const position = (this.object.children.find((child) => child.name === name) as Points).geometry.attributes
        .position.array;
      const frameIndex = this.overlayFrames?.find((frame) => frame.name === name)?.index ?? this.curFrame?.index;
      indices.forEach((val, index) => {
        const projection = this.pixelProjections[name][index];
        if (!projection) return;
        const zValue = position[val * 3 + 2];
        const { minZ, maxZ } = (this.object.children[0] as Points<any, any>).material.uniforms;

        if (
          !(zValue > minZ.value && zValue < maxZ.value) ||
          this.hiddenIndices[name]?.[val] ||
          frameIndex === undefined
        )
          return;

        if (pointInPolyWindingNumber(projection, selectionPoints)) {
          this.processSelection(name, val, selectedSegment, frameIndex);
        }
      });
    });

    // 收集每个类别和实例的数量
    const updateData: Array<Array<Statistic>> = [];
    this.object.children.forEach((child) => {
      const count = (child as Points).geometry.attributes.position.array.length / 3;
      const index = this.overlayFrames?.find((frame) => frame.name === child.name)?.index ?? this.curFrame?.index;
      if (index === undefined) return;
      for (let i = 0; i < count; i++) {
        const category = SseEditor.segmentData[index].category[i];
        if (!category) continue;

        let label = updateData[index]?.find((item) => item.category_name === category);
        if (label) {
          label.num += 1;
        } else {
          label = { category_name: category, num: 1, instances: [] };
          if (updateData[index] === undefined) updateData[index] = [];
          updateData[index].push(label);
        }

        const instanceId = SseEditor.segmentData[index].instance_id[i];
        if (!instanceId) continue;
        let instance = label.instances?.find((item) => item.instance_id === instanceId);
        if (instance) {
          instance.num += 1;
        } else {
          label.instances?.push({ instance_id: instanceId, num: 1 });
        }
      }
    });

    updateData.forEach((statistic, index) => {
      statistic.forEach((label) => label.instances?.sort((a, b) => a.instance_id - b.instance_id));
      SseEditor.segmentData[index].statistic = statistic;
    });
    updateStatistic(updateData);
    initSegments();
  };

  // 更改选中标签的点云颜色
  private setColorForSelection = () => {
    const { curSegMode, selectedSegment } = this.store.getState();
    const color = new Color(curSegMode !== 'remove' && selectedSegment?.color ? selectedSegment.color : '#FFFFFF');
    Object.entries(this.selection).forEach(([key, selection]) => {
      selection.forEach((index) => {
        const baseIndex = index * 3;
        this.colorArray[key][baseIndex] = color.r;
        this.colorArray[key][baseIndex + 1] = color.g;
        this.colorArray[key][baseIndex + 2] = color.b;
      });
    });
  };

  // 框选后更新颜色
  private sseColoring = () => {
    this.setColorForSelection();
    this.updatePointCloudColor();

    // 更新漏标高亮状态（如果处于检测模式）
    const { unlabeledDetection, setUnlabeledDetection } = this.store.getState();
    if (unlabeledDetection?.isActive) {
      // 重新计算漏标数量
      const unlabeledByFrame = this.detectUnlabeledPointsAll();
      const totalCount = Object.values(unlabeledByFrame).reduce((sum, indices) => sum + indices.length, 0);
      setUnlabeledDetection(true, totalCount);
    }
    this.invalidate('unlabeled');

    this.selection = {};
    this.pixelProjections = {};
    this.frustrumIndices = {};
  };

  initPaint = (selectionPoints: number[][]) => {
    this.selectByPolygon(selectionPoints);

    this.sseColoring();
  };

  /**
   * 进入聚焦模式
   * @param selectionPoints 框选区域的屏幕坐标点
   */
  enterFocusMode = (selectionPoints: number[][]) => {
    const { setFocusMode, focusMode } = this.store.getState();

    // 如果已经在聚焦模式，不允许再次进入（需要先退出）
    if (focusMode.isActive) {
      console.warn('已在聚焦模式中，请先退出后再重新选择区域');
      return;
    }

    // 计算框选区域内的点索引
    this.updatePixelProjection(selectionPoints);
    const focusIndices: Record<string, number[]> = {};

    Object.entries(this.frustrumIndices).forEach(([name, indices]) => {
      const position = (this.object.children.find((child) => child.name === name) as Points).geometry.attributes
        .position.array;

      focusIndices[name] = [];

      indices.forEach((val, index) => {
        const projection = this.pixelProjections[name][index];
        if (!projection) return;

        const zValue = position[val * 3 + 2];
        const { minZ, maxZ } = (this.object.children[0] as Points<any, any>).material.uniforms;

        if (!(zValue > minZ.value && zValue < maxZ.value)) return;

        if (pointInPolyWindingNumber(projection, selectionPoints)) {
          focusIndices[name].push(val);
        }
      });
    });

    // 使用独立的聚焦遮罩数组，隐藏非聚焦区域的点
    this.object.children.forEach((child) => {
      const name = child.name;
      const length = this.focusMaskArray[name]?.length ?? 0;
      const focusSet = new Set(focusIndices[name] ?? []);

      // 初始化 focusMaskArray（如果不存在）
      if (!this.focusMaskArray[name]) {
        this.focusMaskArray[name] = new Float32Array(length);
      }

      for (let i = 0; i < length; i++) {
        // 不在聚焦区域内的点设置为遮罩隐藏（1），在聚焦区域内的点设置为可见（0）
        this.focusMaskArray[name][i] = focusSet.has(i) ? 0 : 1;
      }
    });

    // 更新显示
    this.invalidate('focusMask');

    // 更新状态
    setFocusMode(true, focusIndices);

    // 清理临时数据
    this.pixelProjections = {};
    this.frustrumIndices = {};
  };

  /**
   * 退出聚焦模式
   */
  exitFocusMode = () => {
    const { setFocusMode, focusMode } = this.store.getState();

    if (!focusMode.isActive) return;

    // 重置聚焦遮罩数组，恢复所有点的显示
    this.object.children.forEach((child) => {
      const name = child.name;
      if (this.focusMaskArray[name]) {
        // 将所有点的聚焦遮罩设置为 0（可见）
        this.focusMaskArray[name].fill(0);
      }
    });

    // 更新显示
    this.invalidate('focusMask');

    // 更新状态
    setFocusMode(false, {});
  };

  /**
   * 扩展聚焦区域
   * @param selectionPoints 新增区域的屏幕坐标点
   */
  expandFocusRegion = (selectionPoints: number[][]) => {
    const { focusMode, setFocusMode } = this.store.getState();

    if (!focusMode.isActive) return;

    // 计算新区域内的点索引
    this.updatePixelProjection(selectionPoints);
    const newFocusIndices: Record<string, number[]> = { ...focusMode.focusIndices };

    Object.entries(this.frustrumIndices).forEach(([name, indices]) => {
      const position = (this.object.children.find((child) => child.name === name) as Points).geometry.attributes
        .position.array;

      const existingSet = new Set(newFocusIndices[name] ?? []);

      indices.forEach((val, index) => {
        const projection = this.pixelProjections[name][index];
        if (!projection) return;

        const zValue = position[val * 3 + 2];
        const { minZ, maxZ } = (this.object.children[0] as Points<any, any>).material.uniforms;

        if (!(zValue > minZ.value && zValue < maxZ.value)) return;

        if (pointInPolyWindingNumber(projection, selectionPoints)) {
          if (!existingSet.has(val)) {
            if (!newFocusIndices[name]) newFocusIndices[name] = [];
            newFocusIndices[name].push(val);
            // 显示新增的点（使用聚焦遮罩数组）
            if (this.focusMaskArray[name]) {
              this.focusMaskArray[name][val] = 0;
            }
          }
        }
      });
    });

    // 更新显示
    this.invalidate('focusMask');

    // 更新状态
    setFocusMode(true, newFocusIndices);

    // 清理临时数据
    this.pixelProjections = {};
    this.frustrumIndices = {};
  };

  /**
   * 缩小聚焦区域
   * @param selectionPoints 要移除区域的屏幕坐标点
   */
  shrinkFocusRegion = (selectionPoints: number[][]) => {
    const { focusMode, setFocusMode } = this.store.getState();

    if (!focusMode.isActive) return;

    // 计算要移除区域内的点索引
    this.updatePixelProjection(selectionPoints);

    Object.entries(this.frustrumIndices).forEach(([name, indices]) => {
      const position = (this.object.children.find((child) => child.name === name) as Points).geometry.attributes
        .position.array;

      const focusSet = new Set(focusMode.focusIndices[name] ?? []);

      indices.forEach((val, index) => {
        const projection = this.pixelProjections[name][index];
        if (!projection) return;

        const zValue = position[val * 3 + 2];
        const { minZ, maxZ } = (this.object.children[0] as Points<any, any>).material.uniforms;

        if (!(zValue > minZ.value && zValue < maxZ.value)) return;

        if (pointInPolyWindingNumber(projection, selectionPoints)) {
          if (focusSet.has(val)) {
            focusSet.delete(val);
            // 隐藏移除的点（使用聚焦遮罩数组）
            if (this.focusMaskArray[name]) {
              this.focusMaskArray[name][val] = 1;
            }
          }
        }
      });

      // 更新 focusIndices
      focusMode.focusIndices[name] = Array.from(focusSet);
    });

    // 更新显示
    this.invalidate('focusMask');

    // 更新状态
    setFocusMode(true, focusMode.focusIndices);

    // 清理临时数据
    this.pixelProjections = {};
    this.frustrumIndices = {};
  };

  // 计算选中segment的中心点
  getSelectedSegmentCenter = (): Vector3 | null => {
    const { selectedSegment, labelStage } = this.store.getState();
    if (!selectedSegment || !labelStage?.currentSight) return null;

    const center = new Vector3();
    let pointCount = 0;

    const processFrame = (frameIndex: number, frameName: string) => {
      const points = this.object.children.find((child) => child.name === frameName) as Points;
      if (!points) return;

      const positionArray = points.geometry.attributes.position.array;
      const { category, instance_id } = SseEditor.segmentData[frameIndex];

      for (let i = 0; i < category.length; i++) {
        const isNameMatch = category[i] === selectedSegment.name;
        const isSelected =
          isNameMatch &&
          (selectedSegment.instanceId === undefined || instance_id[i] === selectedSegment.instanceId);

        if (isSelected) {
          const x = positionArray[i * 3];
          const y = positionArray[i * 3 + 1];
          const z = positionArray[i * 3 + 2];
          center.add(new Vector3(x, y, z));
          pointCount++;
        }
      }
    };

    if (this.overlayFrames) {
      this.overlayFrames.forEach(({ name: frameName, index: frameIndex }) => {
        processFrame(frameIndex, frameName);
      });
    } else if (this.curFrame) {
      const { index: frameIndex = 0, name: frameName } = this.curFrame;
      processFrame(frameIndex, frameName);
    }

    if (pointCount === 0) return null;

    center.divideScalar(pointCount);
    return center;
  };

  // 根据点云名称获取对应的 frame 索引
  getFrameIndexByPointName = (pointName: string): number | undefined => {
    if (this.overlayFrames) {
      return this.overlayFrames.find((frame) => frame.name === pointName)?.index;
    } else if (this.curFrame && this.curFrame.name === pointName) {
      return this.curFrame.index;
    }
    return undefined;
  };

  // 选中标签的点云高亮
  setSseSelectInfo = () => {
    const { selectedSegment } = this.store.getState();

    if (!selectedSegment) {
      // 清空所有选中
      if (this.overlayFrames) {
        this.overlayFrames.forEach(({ name: frameName }) => {
          if (this.selectArray[frameName]) {
            this.selectArray[frameName].fill(0);
          }
        });
      } else if (this.curFrame) {
        const { name: frameName } = this.curFrame;
        if (this.selectArray[frameName]) {
          this.selectArray[frameName].fill(0);
        }
      }
      this.invalidate('section');
      return;
    }

    if (this.overlayFrames) {
      this.overlayFrames.forEach(({ name: frameName, index: frameIndex }) => {
        SseEditor.segmentData[frameIndex].category.forEach((name, index) => {
          const isNameMatch = name === selectedSegment?.name;
          const isSelected =
            isNameMatch &&
            (selectedSegment?.instanceId === undefined ||
              SseEditor.segmentData[frameIndex].instance_id[index] === selectedSegment.instanceId);
          this.selectArray[frameName][index] = isSelected ? 1 : 0;
        });
      });
    } else if (this.curFrame) {
      const { index: frameIndex = 0, name: frameName } = this.curFrame;
      SseEditor.segmentData[frameIndex].category.forEach((name, index) => {
        const isNameMatch = name === selectedSegment?.name;
        const isSelected =
          isNameMatch &&
          (selectedSegment?.instanceId === undefined ||
            SseEditor.segmentData[frameIndex].instance_id[index] === selectedSegment.instanceId);
        this.selectArray[frameName][index] = isSelected ? 1 : 0;
      });
    }

    this.invalidate('section');
  };

  showOrHide = (name: string, instanceId: number, show: number) => {
    const { getOnlySetting, changeSettingStatus } = this.store.getState();
    const hasOnlySetting = getOnlySetting();
    if (hasOnlySetting) return;
    if (this.overlayFrames) {
      this.overlayFrames.forEach(({ name: frameName, index: frameIndex }) => {
        const { category, instance_id } = SseEditor.segmentData[frameIndex];
        category.forEach((category, inx) => {
          if (category === name && (!instanceId || instance_id[inx] === instanceId)) {
            // 只有一个设置时，检查是否是唯一可见索引且被隐藏；无需单一设置时，直接显示隐藏的索引
            const shouldHide = show && (hasOnlySetting ? this.onlyVisibleIndices[frameName][inx] : true);
            // 只有一个设置时，检查是否是唯一可见索引；无需单一设置时，直接隐藏索引
            const shouldShow =
              !show &&
              (hasOnlySetting
                ? this.onlyVisibleIndices[frameName][inx] && this.hiddenIndices[frameName][inx]
                : this.hiddenIndices[frameName][inx]);

            if (shouldHide) {
              this.updateVisibility(frameName, inx, 1);
            } else if (shouldShow) {
              this.updateVisibility(frameName, inx, 0);
            }
          }
        });
      });
    } else if (this.curFrame) {
      const { index: frameIndex = 0, name: frameName } = this.curFrame;
      const { category, instance_id } = SseEditor.segmentData[frameIndex];
      category.forEach((category, inx) => {
        if (category === name && (!instanceId || instance_id[inx] === instanceId)) {
          // 只有一个设置时，检查是否是唯一可见索引且被隐藏；无需单一设置时，直接显示隐藏的索引
          const shouldHide = show && (hasOnlySetting ? this.onlyVisibleIndices[frameName][inx] : true);
          // 只有一个设置时，检查是否是唯一可见索引；无需单一设置时，直接隐藏索引
          const shouldShow =
            !show &&
            (hasOnlySetting
              ? this.onlyVisibleIndices[frameName][inx] && this.hiddenIndices[frameName][inx]
              : this.hiddenIndices[frameName][inx]);

          if (shouldHide) {
            this.updateVisibility(frameName, inx, 1);
          } else if (shouldShow) {
            this.updateVisibility(frameName, inx, 0);
          }
        }
      });
    }

    // 更新数据
    changeSettingStatus(name, instanceId, show, 'isHide');
    this.invalidate('position');
  };

  private addOnlyShowIndex = (name: string, index: number) => {
    this.onlyVisibleIndices[name][index] = 1;
  };

  private updateVisibility = (name: string, index: number, isVisible: number) => {
    this.hiddenIndices[name][index] = isVisible;
    this.clipArray[name][index] = isVisible;
  };

  showSelfOnly = (name: string, instanceId: number, isCancel: number) => {
    const { getOnlySetting, getSetting, changeSettingStatus } = this.store.getState();
    changeSettingStatus(name, instanceId, isCancel, 'isOnlyShow');
    const hasOnlySetting = getOnlySetting();
    if (this.overlayFrames) {
      this.overlayFrames.forEach(({ name: frameName, index: frameIndex }) => {
        const { category, instance_id } = SseEditor.segmentData[frameIndex];
        category.forEach((category, inx) => {
          const instanceID = instance_id[inx];
          const isTarget = category === name && (!instanceId || instanceID === instanceId);
          if (!isCancel) {
            if (hasOnlySetting) {
              if (isTarget && this.onlyVisibleIndices[frameName][inx]) {
                this.updateVisibility(frameName, inx, 1);
                this.onlyVisibleIndices[frameName][inx] = 0;
              }
            } else {
              // 全部显示
              const isHide = getSetting(category, instanceID)?.isHide;
              if (isHide) {
                this.updateVisibility(frameName, inx, 1);
              } else {
                this.updateVisibility(frameName, inx, 0);
              }
            }
          } else {
            // 唯显，是选中
            if (isTarget) {
              this.updateVisibility(frameName, inx, 0);
              this.addOnlyShowIndex(frameName, inx);
            } else {
              // 不是选中，在 onlyVisibleIndices 则显示，不在 onlyVisibleIndices 则隐藏
              if (this.onlyVisibleIndices[frameName][inx]) {
                this.updateVisibility(frameName, inx, 0);
              } else {
                this.updateVisibility(frameName, inx, 1);
              }
            }
          }
        });
        if (!isCancel && !hasOnlySetting) {
          this.onlyVisibleIndices[frameName].fill(0);
        }
      });
    } else if (this.curFrame) {
      const { index: frameIndex = 0, name: frameName } = this.curFrame;
      SseEditor.segmentData[frameIndex].category.forEach((category, inx) => {
        const instanceID = SseEditor.segmentData[frameIndex].instance_id[inx];
        const isTarget = category === name && (!instanceId || instanceID === instanceId);
        if (!isCancel) {
          if (hasOnlySetting) {
            if (isTarget && this.onlyVisibleIndices[frameName][inx]) {
              this.updateVisibility(frameName, inx, 1);
              this.onlyVisibleIndices[frameName][inx] = 0;
            }
          } else {
            // 全部显示
            const isHide = getSetting(category, instanceID)?.isHide;
            if (isHide) {
              this.updateVisibility(frameName, inx, 1);
            } else {
              this.updateVisibility(frameName, inx, 0);
            }
          }
        } else {
          // 唯显，是选中
          if (isTarget) {
            this.updateVisibility(frameName, inx, 0);
            this.addOnlyShowIndex(frameName, inx);
          } else {
            // 不是选中，在 onlyVisibleIndices 则显示，不在 onlyVisibleIndices 则隐藏
            if (this.onlyVisibleIndices[frameName][inx]) {
              this.updateVisibility(frameName, inx, 0);
            } else {
              this.updateVisibility(frameName, inx, 1);
            }
          }
        }
      });
      if (!isCancel && !hasOnlySetting) {
        this.onlyVisibleIndices[frameName].fill(0);
      }
    }
    this.invalidate('position');
  };

  private handleSettings = (
    { category, instance_id }: Segment,
    { name, instanceId }: { name: string; instanceId: number },
    { frameName, frameIndex }: { frameName: string; frameIndex: number },
    { isMatch, isEmpty }: { isMatch: boolean; isEmpty: boolean }
  ) => {
    category.forEach((categoryName, index) => {
      if (categoryName === name && (!instanceId || instance_id[index] === instanceId)) {
        this.selectArray[frameName][index] = isMatch ? 0 : isEmpty ? 1 : this.selectArray[frameName][index];
        this.categoryID[frameName][index] = 0;
        SseEditor.segmentData[frameIndex].category[index] = '';
        SseEditor.segmentData[frameIndex].instance_id[index] = 0;
        // 显示隐藏的索引
        if (this.hiddenIndices[frameName][index]) {
          this.updateVisibility(frameName, index, 0);
        }
        // 如果只有可见的索引，则重置
        if (this.onlyVisibleIndices[frameName][index]) {
          this.onlyVisibleIndices[frameName][index] = 0;
        }
      }
    });
  };

  private handleStatistic = (oldStatistic: Array<Statistic>, category: string, instanceId: number) => {
    const statistic = cloneDeep(oldStatistic);

    const labelInx = statistic.findIndex((item) => item.category_name === category);
    if (labelInx === -1) return;

    // 如果存在 instanceId 和对应的 instances，更新实例数量
    const instanceInx = statistic[labelInx].instances?.findIndex((item) => item.instance_id === instanceId);

    if (instanceInx !== undefined && instanceInx !== -1) {
      const num = statistic[labelInx].instances?.[instanceInx].num;
      if (num) statistic[labelInx].num -= num;
      statistic[labelInx].instances?.splice(instanceInx, 1);
    } else {
      // 如果没有提供 instanceId 或不存在对应的 instances，将数量置为0
      statistic[labelInx].num = 0;
    }

    // 如果数量为0，从集合中删除该标签
    if (statistic[labelInx].num === 0) {
      statistic.splice(labelInx, 1);
    }
    return statistic;
  };

  delete = async (name: string, instanceId: number) => {
    const { selectedSegment, statistic, overlayFrames, updateStatistic, updateIds } = this.store.getState();
    const isMatch = selectedSegment?.name === name && selectedSegment?.instanceId === instanceId;
    const isEmpty = selectedSegment?.name === '';

    const processFrame = (frameIndex: number, frameName: string) => {
      this.handleSettings(
        SseEditor.segmentData[frameIndex],
        { name, instanceId },
        { frameName, frameIndex },
        { isMatch, isEmpty }
      );
      if (statistic[frameIndex]) {
        const newStatistic = this.handleStatistic(statistic[frameIndex], name, instanceId);
        if (newStatistic) {
          updateData[frameIndex] = newStatistic;
          SseEditor.segmentData[frameIndex].statistic = newStatistic;
        }
      }
    };

    const updateData: Array<Array<Statistic>> = [];
    // 判断是否叠帧
    if (overlayFrames) {
      const { startIndex, num } = overlayFrames;
      for (let i = 0; i <= num; i++) {
        const frameIndex = startIndex + i;
        const frameName = this.overlayFrames?.find((frame) => frame.index === frameIndex)?.name ?? '';
        processFrame(frameIndex, frameName);
      }
    } else {
      const { index: frameIndex = 0, name: frameName = '' } = this.curFrame ?? {};
      processFrame(frameIndex, frameName);
    }

    this.updatePointCloudColor();

    // 更新
    await updateStatistic(updateData);
    if (!overlayFrames) {
      updateIds();
    }
  };

  reset = () => {
    const { resetCurSegLabel, setCurrentTool, setSegMode } = this.store.getState();
    this.object.children.forEach((child) => {
      const geometry = (child as Points).geometry;
      // 清除分割属性
      geometry.deleteAttribute('sseColor');
      geometry.deleteAttribute('categoryID');
      geometry.deleteAttribute('hidePoint');
      geometry.deleteAttribute('selectPoint');
    });
    resetCurSegLabel();
    setCurrentTool('select');
    setSegMode('add');
  };

  /**
   * 检测未标注的点云索引（支持单帧和叠帧模式）
   * @returns 按帧名称分组的未标注点索引 { frameName: indices[] }
   */
  public detectUnlabeledPointsAll = (): Record<string, number[]> => {
    const result: Record<string, number[]> = {};

    // 叠帧模式
    if (this.overlayFrames && this.overlayFrames.length > 0) {
      this.overlayFrames.forEach(({ name }) => {
        const categoryID = this.categoryID[name];
        if (!categoryID) return;

        const unlabeledIndices: number[] = [];
        for (let i = 0; i < categoryID.length; i++) {
          if (categoryID[i] === 0) {
            unlabeledIndices.push(i);
          }
        }
        result[name] = unlabeledIndices;
      });
    } 
    // 单帧模式
    else if (this.curFrame) {
      const { name } = this.curFrame;
      const categoryID = this.categoryID[name];
      if (categoryID) {
        const unlabeledIndices: number[] = [];
        for (let i = 0; i < categoryID.length; i++) {
          if (categoryID[i] === 0) {
            unlabeledIndices.push(i);
          }
        }
        result[name] = unlabeledIndices;
      }
    }

    return result;
  };

  /**
   * 高亮显示未标注的点云（支持单帧和叠帧模式）
   * @param highlight 是否高亮，true=高亮未标注点，false=取消高亮
   */
  public highlightUnlabeledPoints = (highlight: boolean): number => {
    const unlabeledByFrame = this.detectUnlabeledPointsAll();
    let totalCount = 0;

    // 遍历所有帧（单帧或叠帧）
    Object.entries(unlabeledByFrame).forEach(([name, unlabeledIndices]) => {
      const length = this.categoryID[name]?.length ?? 0;
      const points = this.object.children.find((child) => child.name === name) as Points;

      // 初始化 unlabeledHighlightArray（如果不存在）
      if (!this.unlabeledHighlightArray[name]) {
        this.unlabeledHighlightArray[name] = new Float32Array(length).fill(0);
      }

      if (highlight) {
        // 高亮未标注点：设置 unlabeledHighlightArray 为 1
        // 先重置为 0，再设置未标注点为 1
        this.unlabeledHighlightArray[name].fill(0);
        unlabeledIndices.forEach((index) => {
          this.unlabeledHighlightArray[name][index] = 1;
        });

        // 自动扩展 Z 轴范围以包含所有未标注点
        if (points && unlabeledIndices.length > 0) {
          this.autoExpandZRangeForUnlabeled(points, unlabeledIndices);
        }

        totalCount += unlabeledIndices.length;
      } else {
        // 取消高亮：重置 unlabeledHighlightArray 为 0
        this.unlabeledHighlightArray[name].fill(0);
      }

      // 更新该帧的 unlabeled attribute
      if (points) {
        this.invalidatePoints('unlabeled', points);
      }
    });

    // 设置全局 unlabeledMode uniform，控制其他点的透明度
    this.object.children.forEach((child) => {
      const material = (child as Points).material as any;
      if (material?.uniforms?.unlabeledMode) {
        material.uniforms.unlabeledMode.value = highlight ? 1.0 : 0.0;
      }
    });

    return totalCount;
  };

  /**
   * 自动扩展 Z 轴范围以包含所有未标注点
   * @param points 点云对象
   * @param unlabeledIndices 未标注点的索引数组
   */
  private autoExpandZRangeForUnlabeled = (points: Points, unlabeledIndices: number[]): void => {
    const material = points.material as any;
    if (!material?.uniforms?.minZ || !material?.uniforms?.maxZ) return;

    const positionArray = points.geometry.attributes.position.array;
    const currentMinZ = material.uniforms.minZ.value;
    const currentMaxZ = material.uniforms.maxZ.value;

    // 计算未标注点的 Z 轴范围
    let unlabeledMinZ = Number.MAX_SAFE_INTEGER;
    let unlabeledMaxZ = Number.MIN_SAFE_INTEGER;

    unlabeledIndices.forEach((idx) => {
      const z = positionArray[idx * 3 + 2];
      if (z < unlabeledMinZ) unlabeledMinZ = z;
      if (z > unlabeledMaxZ) unlabeledMaxZ = z;
    });

    // 检查是否需要扩展范围
    let needExpand = false;
    let newMinZ = currentMinZ;
    let newMaxZ = currentMaxZ;

    if (unlabeledMinZ < currentMinZ) {
      // 向下扩展，留一点余量
      newMinZ = Math.floor(unlabeledMinZ) - 1;
      needExpand = true;
    }

    if (unlabeledMaxZ > currentMaxZ) {
      // 向上扩展，留一点余量
      newMaxZ = Math.ceil(unlabeledMaxZ) + 1;
      needExpand = true;
    }

    if (needExpand) {
      console.log(`自动扩展 Z 轴范围: [${currentMinZ}, ${currentMaxZ}] -> [${newMinZ}, ${newMaxZ}]`);
      
      // 更新所有点云对象的 Z 轴范围
      this.object.children.forEach((child) => {
        const mat = (child as Points).material as any;
        if (mat?.uniforms?.minZ && mat?.uniforms?.maxZ) {
          mat.uniforms.minZ.value = newMinZ;
          mat.uniforms.maxZ.value = newMaxZ;
        }
      });
    }
  };
}
